import {
  createWorkItemProjectionState,
  reduceWorkItemProjection,
} from '@web/lib/remoteWorkItemModel';
import type {
  CanonicalProjectEvent,
  ProjectedArtifact,
  ProjectedRun,
  ProjectorMode,
  ProjectViewState,
} from './types';

const RUN_STATUS_BY_EVENT: Record<string, ProjectedRun['status']> = {
  'run.attempt_created': 'running',
  'run.attempt_started': 'running',
  'run.completed': 'completed',
  'run.failed': 'failed',
  'run.deadline_reached': 'failed',
  'run.cancelled': 'cancelled',
  'run.interrupted': 'interrupted',
  'runtime.interrupted': 'interrupted',
};

const CROSS_LANE_MATCH_WINDOW_SECONDS = 120;
const MAX_CANONICAL_EVENTS = 5000;
const MAX_SEEN_EVENT_IDS = 10000;
const MAX_LEGACY_STEPS = 5000;
const MAX_UNKNOWN_EVENTS = 500;
const MAX_ARTIFACT_RUNS = 1000;

function appendBounded<T>(values: T[], value: T, limit: number): T[] {
  const next = [...values, value];
  return next.length > limit ? next.slice(next.length - limit) : next;
}

function appendSeenEvent(
  seenEventIds: Record<string, true>,
  eventId: string
): Record<string, true> {
  const next = { ...seenEventIds, [eventId]: true as const };
  const overflow = Object.keys(next).length - MAX_SEEN_EVENT_IDS;
  if (overflow <= 0) return next;
  const retained = Object.keys(next).slice(overflow);
  return Object.fromEntries(retained.map((id) => [id, true])) as Record<
    string,
    true
  >;
}

export function retainRecentRunRecords<T>(
  recordsByRun: Record<string, T>,
  currentRunId: string
): Record<string, T> {
  const runIds = Object.keys(recordsByRun);
  if (runIds.length <= MAX_ARTIFACT_RUNS) return recordsByRun;
  const retained = runIds
    .filter((runId) => runId !== currentRunId)
    .slice(-(MAX_ARTIFACT_RUNS - 1));
  retained.push(currentRunId);
  return Object.fromEntries(
    retained.map((runId) => [runId, recordsByRun[runId]])
  );
}

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, item]) => [key, stableValue(item)])
    );
  }
  return value;
}

function sameData(left: unknown, right: unknown): boolean {
  return (
    JSON.stringify(stableValue(left)) === JSON.stringify(stableValue(right))
  );
}

function findEquivalentCrossLaneStep(
  state: ProjectViewState,
  event: CanonicalProjectEvent,
  data: unknown
): number {
  if (!event.legacyStep) return -1;
  const eventTimestamp = Date.parse(event.createdAt) / 1000;
  if (!Number.isFinite(eventTimestamp)) return -1;
  const eventIsCanonical = event.source === 'canonical';
  for (let index = state.legacySteps.length - 1; index >= 0; index -= 1) {
    const step = state.legacySteps[index];
    if (
      step.projectId === event.projectId &&
      step.taskId === event.runId &&
      step.step === event.legacyStep &&
      (step.source === 'canonical') !== eventIsCanonical &&
      !step.crossLaneEventIds?.length &&
      step.timestamp !== null &&
      Math.abs(step.timestamp - eventTimestamp) <=
        CROSS_LANE_MATCH_WINDOW_SECONDS &&
      sameData(step.data, data)
    ) {
      return index;
    }
  }
  return -1;
}

function hasEquivalentOpenAsk(
  state: ProjectViewState,
  event: CanonicalProjectEvent,
  data: unknown
): boolean {
  for (let index = state.legacySteps.length - 1; index >= 0; index -= 1) {
    const step = state.legacySteps[index];
    if (step.projectId !== event.projectId || step.taskId !== event.runId)
      continue;
    if (step.step === 'end' || step.step === 'human_reply') return false;
    if (step.step === 'ask' && sameData(step.data, data)) return true;
  }
  return false;
}

export function createProjectViewState(
  projectId: string,
  mode: ProjectorMode
): ProjectViewState {
  return {
    projectId,
    mode,
    seenEventIds: {},
    currentCursor: 0,
    eventsTruncated: false,
    lastSyncedAt: null,
    needsResync: false,
    resyncReason: null,
    resyncTargetCursor: null,
    runs: {},
    artifactsByRun: {},
    artifactManifestsByRun: {},
    legacySteps: [],
    canonicalEvents: [],
    workItemProjection: createWorkItemProjectionState(),
    unknownEvents: [],
  };
}

export function completeProjectViewResync(
  state: ProjectViewState,
  authoritativeCursor: number
): ProjectViewState {
  const deltaRecoverable =
    state.resyncReason?.startsWith('cloud_cursor_gap:') ||
    state.resyncReason?.startsWith('run_sequence_gap:');
  if (
    !state.needsResync ||
    !deltaRecoverable ||
    state.currentCursor < authoritativeCursor ||
    (state.resyncTargetCursor !== null &&
      state.currentCursor < state.resyncTargetCursor)
  ) {
    return state;
  }
  return {
    ...state,
    needsResync: false,
    resyncReason: null,
    resyncTargetCursor: null,
  };
}

export function reduceProjectView(
  state: ProjectViewState,
  event: CanonicalProjectEvent
): ProjectViewState {
  return reduceProjectViewInternal(state, event, false);
}

/**
 * Batch replay owns its accumulator exclusively, so it can reuse the mutable
 * WorkItem indexes without cloning the full projection for every event. The
 * public reducer above remains referentially pure for React/concurrent calls.
 */
export function reduceProjectViewOwned(
  state: ProjectViewState,
  event: CanonicalProjectEvent
): ProjectViewState {
  return reduceProjectViewInternal(state, event, true);
}

export function compactOwnedProjectView(
  state: ProjectViewState
): ProjectViewState {
  const eventIds = Object.keys(state.seenEventIds);
  if (eventIds.length <= MAX_SEEN_EVENT_IDS) return state;
  const retained = eventIds.slice(eventIds.length - MAX_SEEN_EVENT_IDS);
  return {
    ...state,
    seenEventIds: Object.fromEntries(
      retained.map((eventId) => [eventId, true])
    ) as Record<string, true>,
  };
}

function reduceProjectViewInternal(
  state: ProjectViewState,
  event: CanonicalProjectEvent,
  ownsWorkItemProjection: boolean
): ProjectViewState {
  if (state.seenEventIds[event.eventId]) return state;
  if (event.projectId !== state.projectId) {
    return {
      ...state,
      needsResync: true,
      resyncReason: `project_scope_mismatch:${event.projectId}`,
      resyncTargetCursor: null,
    };
  }
  if (
    event.source === 'canonical' &&
    event.cloudCursor !== null &&
    event.cloudCursor <= state.currentCursor
  ) {
    return state;
  }

  let gapReason: string | null = null;
  if (
    event.source === 'canonical' &&
    event.cloudCursor !== null &&
    event.cloudCursor > state.currentCursor + 1 &&
    (state.currentCursor > 0 || state.mode === 'live')
  ) {
    gapReason = `cloud_cursor_gap:${state.currentCursor + 1}:${event.cloudCursor}`;
  }
  const previousRun = state.runs[event.runId];
  if (
    event.source === 'canonical' &&
    event.runSequence > (previousRun?.lastSequence || 0) + 1 &&
    (previousRun !== undefined || state.mode === 'live')
  ) {
    gapReason = `run_sequence_gap:${event.runId}:${(previousRun?.lastSequence || 0) + 1}:${event.runSequence}`;
  }
  if (gapReason) {
    return {
      ...state,
      needsResync: true,
      resyncReason: gapReason,
      resyncTargetCursor: event.cloudCursor,
    };
  }

  const candidateStatus =
    RUN_STATUS_BY_EVENT[event.eventType] ||
    (event.legacyStep === 'end' &&
    (event.source === 'chat_step_v1' || event.eventType.startsWith('legacy.'))
      ? 'completed'
      : previousRun?.status || 'running');
  const status =
    previousRun &&
    previousRun.status !== 'running' &&
    previousRun.status !== 'interrupted' &&
    candidateStatus === 'running'
      ? previousRun.status
      : candidateStatus;
  const run: ProjectedRun = {
    ...previousRun,
    ...(event.origin ? { origin: event.origin } : {}),
    runId: event.runId,
    status,
    lastSequence:
      event.source === 'canonical'
        ? Math.max(previousRun?.lastSequence || 0, event.runSequence)
        : previousRun?.lastSequence || 0,
    runVersion:
      event.source === 'canonical'
        ? Math.max(previousRun?.runVersion || 0, event.runVersion)
        : previousRun?.runVersion || 0,
    updatedAt: event.createdAt,
  };

  const legacyStepId =
    (event.payload.__legacy_step_id as number | string | undefined) ||
    event.eventId;
  const legacyData = event.payload.__legacy_data ?? event.payload;
  const workItemProjection = reduceWorkItemProjection(
    ownsWorkItemProjection
      ? state.workItemProjection || createWorkItemProjectionState()
      : structuredClone(
          state.workItemProjection || createWorkItemProjectionState()
        ),
    event
  );
  let artifactsByRun = state.artifactsByRun;
  let artifactManifestsByRun = state.artifactManifestsByRun || {};
  if (event.eventType === 'artifact.manifest.finalized') {
    const rawArtifacts = Array.isArray(event.payload.artifacts)
      ? event.payload.artifacts
      : [];
    const artifacts: ProjectedArtifact[] = rawArtifacts.flatMap((raw) => {
      if (!raw || typeof raw !== 'object') return [];
      const value = raw as Record<string, unknown>;
      const relativePath =
        typeof value.relativePath === 'string' ? value.relativePath : '';
      const name =
        typeof value.filename === 'string'
          ? value.filename
          : relativePath.split('/').filter(Boolean).at(-1) || '';
      if (!relativePath || !name) return [];
      return [
        {
          artifactId:
            typeof value.artifact_id === 'string'
              ? value.artifact_id
              : `${event.runId}:${relativePath}`,
          runId: event.runId,
          name,
          relativePath,
          changeType:
            value.changeType === 'generated' ? 'generated' : 'changed',
          size:
            typeof value.size === 'number' && Number.isFinite(value.size)
              ? value.size
              : null,
          modifiedAt:
            typeof value.modifiedAt === 'number' &&
            Number.isFinite(value.modifiedAt)
              ? value.modifiedAt
              : null,
          uploadPolicy:
            typeof value.uploadPolicy === 'string' ? value.uploadPolicy : null,
          localPathAvailable: value.localPathAvailable === true,
        },
      ];
    });
    const previousById = new Map(
      (state.artifactsByRun[event.runId] || []).map((artifact) => [
        artifact.artifactId,
        artifact,
      ])
    );
    artifactsByRun = retainRecentRunRecords(
      {
        ...state.artifactsByRun,
        [event.runId]: artifacts.map((artifact) => ({
          ...artifact,
          assetRef: previousById.get(artifact.artifactId)?.assetRef,
        })),
      },
      event.runId
    );
    artifactManifestsByRun = retainRecentRunRecords(
      {
        ...artifactManifestsByRun,
        [event.runId]: {
          scanStatus:
            typeof event.payload.scan_status === 'string'
              ? event.payload.scan_status
              : 'complete',
          truncated: event.payload.truncated === true,
          artifactCount:
            typeof event.payload.artifact_count === 'number'
              ? event.payload.artifact_count
              : artifacts.length,
        },
      },
      event.runId
    );
  }
  if (event.eventType === 'artifact.uploaded') {
    const artifactId =
      typeof event.payload.artifact_id === 'string'
        ? event.payload.artifact_id
        : '';
    const rawAsset =
      event.payload.asset_ref && typeof event.payload.asset_ref === 'object'
        ? (event.payload.asset_ref as Record<string, unknown>)
        : null;
    const key = typeof rawAsset?.key === 'string' ? rawAsset.key : '';
    if (artifactId && key) {
      artifactsByRun = retainRecentRunRecords(
        {
          ...artifactsByRun,
          [event.runId]: (artifactsByRun[event.runId] || []).map((artifact) =>
            artifact.artifactId === artifactId
              ? {
                  ...artifact,
                  assetRef: {
                    key,
                    chatFileId:
                      typeof rawAsset?.chat_file_id === 'number'
                        ? rawAsset.chat_file_id
                        : undefined,
                    bucket:
                      typeof rawAsset?.bucket === 'string'
                        ? rawAsset.bucket
                        : undefined,
                    filename:
                      typeof rawAsset?.filename === 'string'
                        ? rawAsset.filename
                        : undefined,
                    size:
                      typeof rawAsset?.size === 'number'
                        ? rawAsset.size
                        : undefined,
                    contentType:
                      typeof rawAsset?.content_type === 'string'
                        ? rawAsset.content_type
                        : undefined,
                  },
                }
              : artifact
          ),
        },
        event.runId
      );
    }
  }
  const hasStableStep =
    event.legacyStep !== null &&
    state.legacySteps.some(
      (step) =>
        step.projectId === event.projectId &&
        step.taskId === event.runId &&
        String(step.stepId) === String(legacyStepId)
    );
  const equivalentIndex = hasStableStep
    ? -1
    : findEquivalentCrossLaneStep(state, event, legacyData);
  const hasStep =
    hasStableStep ||
    equivalentIndex >= 0 ||
    (event.legacyStep === 'ask' &&
      hasEquivalentOpenAsk(state, event, legacyData));

  let legacySteps = state.legacySteps;
  if (equivalentIndex >= 0) {
    const step = state.legacySteps[equivalentIndex];
    legacySteps = state.legacySteps.map((candidate, index) =>
      index === equivalentIndex
        ? {
            ...step,
            crossLaneEventIds: [
              ...(step.crossLaneEventIds || []),
              event.eventId,
            ],
          }
        : candidate
    );
  } else if (event.legacyStep && !hasStep) {
    legacySteps = appendBounded(
      state.legacySteps,
      {
        eventId: event.eventId,
        stepId: legacyStepId,
        taskId: event.runId,
        projectId: event.projectId,
        step: event.legacyStep,
        data: legacyData,
        timestamp: Date.parse(event.createdAt) / 1000 || null,
        runSequence: event.runSequence,
        cloudCursor: event.cloudCursor,
        source: event.source,
      },
      MAX_LEGACY_STEPS
    );
  }
  let canonicalEvents = state.canonicalEvents;
  if (event.source === 'canonical') {
    if (ownsWorkItemProjection) {
      canonicalEvents.push(event);
      const overflow = canonicalEvents.length - MAX_CANONICAL_EVENTS;
      if (overflow > 0) canonicalEvents.splice(0, overflow);
    } else {
      canonicalEvents = appendBounded(
        canonicalEvents,
        event,
        MAX_CANONICAL_EVENTS
      );
    }
  }
  const knownEvent =
    Boolean(event.legacyStep) ||
    Boolean(RUN_STATUS_BY_EVENT[event.eventType]) ||
    event.eventType.startsWith('artifact.') ||
    workItemProjection.unknownEventIds.includes(event.eventId) ||
    typeof event.payload.item_id === 'string' ||
    typeof event.payload.itemId === 'string';
  const unknownEvents = knownEvent
    ? state.unknownEvents
    : appendBounded(state.unknownEvents, event, MAX_UNKNOWN_EVENTS);

  let seenEventIds: Record<string, true>;
  let runs: Record<string, ProjectedRun>;
  if (ownsWorkItemProjection) {
    state.seenEventIds[event.eventId] = true;
    state.runs[event.runId] = run;
    seenEventIds = state.seenEventIds;
    runs = state.runs;
  } else {
    seenEventIds = appendSeenEvent(state.seenEventIds, event.eventId);
    runs = { ...state.runs, [event.runId]: run };
  }

  return {
    ...state,
    seenEventIds,
    currentCursor:
      event.cloudCursor === null
        ? state.currentCursor
        : Math.max(state.currentCursor, event.cloudCursor),
    lastSyncedAt: event.createdAt,
    runs,
    artifactsByRun,
    artifactManifestsByRun,
    legacySteps,
    canonicalEvents,
    workItemProjection,
    unknownEvents,
  };
}
